import React, { useEffect, useMemo, useRef, useState } from 'react';

type Square = {
  id: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  rotation: number;
  targetRotation: number;
  size: number;
};

export const AssemblingSquares: React.FC = () => {
  const motionEnabled = useMemo(() => {
    try {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
      return !reduce && !coarse;
    } catch {
      return true;
    }
  }, []);

  const [squares, setSquares] = useState<Square[]>([]);
  const [animationPhase, setAnimationPhase] = useState(0); // 0: scattered, 1: assembling, 2: assembled, 3: dispersing
  const [cycleCount, setCycleCount] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  const totalSquares = 30;
  // Keep patterns stable across renders, otherwise timers reset every frame.
  const centerX = 750; 
  const centerY = 800; 

  const patterns = useRef(
    [
      {
        positions: Array.from({ length: 30 }, (_, i) => {
          const row = Math.floor(i / 5);
          const col = i % 5;
          return {
            x: centerX + (col - 2) * 55,
            y: centerY + (row - 2.5) * 55,
            rotation: 0,
          };
        }),
      },
      {
        positions: Array.from({ length: totalSquares }, (_, i) => {
          if (i < 5) return { x: centerX, y: centerY - 120 + i * 60, rotation: 0 };
          if (i < 10) return { x: centerX - 180 + (i - 5) * 60, y: centerY, rotation: 0 };
          if (i < 15) return { x: centerX - 150 + (i - 10) * 50, y: centerY - 150 + (i - 10) * 50, rotation: 45 };
          return { x: centerX + 150 - (i - 15) * 50, y: centerY - 150 + (i - 15) * 50, rotation: -45 };
        }),
      },
      {
        positions: Array.from({ length: totalSquares }, (_, i) => {
          const angle = (i / totalSquares) * Math.PI * 2;
          const radius = 150;
          return {
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius,
            rotation: (angle * 180) / Math.PI,
          };
        }),
      },
      {
        positions: Array.from({ length: totalSquares }, (_, i) => {
          if (i < 10) {
            const row = Math.floor(i / 3);
            const col = i % 3;
            return { x: centerX - 200 + col * 50, y: centerY - 50 + row * 50, rotation: 0 };
          }
          const row = Math.floor((i - 10) / 3);
          const col = (i - 10) % 3;
          return { x: centerX + 50 + col * 50, y: centerY + 50 + row * 50, rotation: 45 };
        }),
      },
      {
        positions: Array.from({ length: totalSquares }, (_, i) => {
          const angle = i * 0.5;
          const radius = 50 + i * 15;
          return {
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius,
            rotation: (angle * 180) / Math.PI,
          };
        }),
      },
    ] as const
  ).current;

  useEffect(() => {
    const initialSquares: Square[] = Array.from({ length: totalSquares }, (_, i) => {
      const angle = (i / totalSquares) * Math.PI * 2;
      const radius = 30;
      const x = Math.max(100, Math.min(900, centerX + Math.cos(angle) * radius));
      const y = Math.max(100, Math.min(900, centerY + Math.sin(angle) * radius));
      return {
        id: i,
        x,
        y,
        targetX: x,
        targetY: y,
        rotation: Math.random() * 360,
        targetRotation: Math.random() * 360,
        size: 40 + Math.random() * 20,
      };
    });
    setSquares(initialSquares);
  }, []);

  useEffect(() => {
    if (!motionEnabled) return undefined;
    if (squares.length === 0) return undefined;

    const animate = () => {
      setSquares(prev =>
        prev.map(square => {
          const dx = square.targetX - square.x;
          const dy = square.targetY - square.y;
          const dRot = square.targetRotation - square.rotation;

          const speed = animationPhase === 1 ? 0.05 : animationPhase === 3 ? 0.04 : 0.027;
          const rotSpeed = animationPhase === 3 ? 0.05 : 0.06;

          let x = Math.abs(dx) < 0.1 ? square.targetX : square.x + dx * speed;
          let y = Math.abs(dy) < 0.1 ? square.targetY : square.y + dy * speed;
          const rotation = Math.abs(dRot) < 0.1 ? square.targetRotation : square.rotation + dRot * rotSpeed;

          x = Math.max(50, Math.min(950, x));
          if (square.targetY < 1000) {
            y = Math.max(50, Math.min(950, y));
          } else {
            y = Math.max(1050, Math.min(1950, y));
          }

          return { ...square, x, y, rotation };
        })
      );

      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) window.cancelAnimationFrame(animationFrameRef.current);
    };
  }, [motionEnabled, animationPhase, squares.length]);

  useEffect(() => {
    if (!motionEnabled) return undefined;
    if (squares.length === 0) return undefined;

    const timeouts: Array<number> = [];
    const scheduleCycle = (cycle: number) => {
      const t1 = window.setTimeout(() => {
        setAnimationPhase(1);
        const pattern = patterns[cycle % patterns.length];
        setSquares(prev =>
          prev.map((sq, i) => ({
            ...sq,
            targetX: pattern.positions[i]?.x ?? sq.targetX,
            targetY: pattern.positions[i]?.y ?? sq.targetY,
            targetRotation: pattern.positions[i]?.rotation ?? 0,
          }))
        );

        const t2 = window.setTimeout(() => {
          setAnimationPhase(2);

          const t3 = window.setTimeout(() => {
            setAnimationPhase(3);
            setSquares(prev => {
              const squaresPerScreen = Math.ceil(totalSquares / 2);
              const cols = Math.ceil(Math.sqrt(squaresPerScreen));
              const rows = Math.ceil(squaresPerScreen / cols);
              const screenWidth = 900; 
              const screenHeight = 950; 
              const cellWidth = screenWidth / cols;
              const cellHeight = screenHeight / rows;

              return prev.map((sq, i) => {
                const isFirstScreen = i < squaresPerScreen;
                const indexInScreen = isFirstScreen ? i : i - squaresPerScreen;
                const col = indexInScreen % cols;
                const row = Math.floor(indexInScreen / cols);

                const offsetX = (Math.random() - 0.5) * cellWidth * 0.6;
                const offsetY = (Math.random() - 0.5) * cellHeight * 0.6;

                let targetX = 50 + col * cellWidth + cellWidth / 2 + offsetX;
                let targetY = isFirstScreen
                  ? 50 + row * cellHeight + cellHeight / 2 + offsetY
                  : 1050 + row * cellHeight + cellHeight / 2 + offsetY;

                targetX = Math.max(50, Math.min(950, targetX));
                targetY = isFirstScreen ? Math.max(50, Math.min(950, targetY)) : Math.max(1050, Math.min(1950, targetY));

                return {
                  ...sq,
                  targetX,
                  targetY,
                  targetRotation: Math.random() * 360,
                };
              });
            });

            const t4 = window.setTimeout(() => {
              setAnimationPhase(0);
              setCycleCount(cycle + 1);
              scheduleCycle(cycle + 1);
            }, 1000);
            timeouts.push(t4);
          }, 4500);
          timeouts.push(t3);
        }, 1000);
        timeouts.push(t2);
      }, 300);
      timeouts.push(t1);
    };

    scheduleCycle(cycleCount);

    return () => {
      timeouts.forEach(t => window.clearTimeout(t));
    };
  }, [motionEnabled, cycleCount, squares.length]);

  return (
    <div className="geometric-background geometric-assembling-squares">
      <svg className="assembling-squares-svg" viewBox="0 0 1000 2000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id="assembleGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(93, 99, 255, 0.5)" />
            <stop offset="100%" stopColor="rgba(138, 43, 226, 0.4)" />
          </linearGradient>
          <linearGradient id="assembleGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(138, 43, 226, 0.45)" />
            <stop offset="100%" stopColor="rgba(75, 192, 192, 0.35)" />
          </linearGradient>
          <linearGradient id="assembleGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(75, 192, 192, 0.4)" />
            <stop offset="100%" stopColor="rgba(255, 99, 132, 0.3)" />
          </linearGradient>
          <filter id="assembleGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {squares.map((square, i) => (
          <g key={square.id} transform={`translate(${square.x}, ${square.y}) rotate(${square.rotation})`}>
            <rect
              x={-square.size / 2}
              y={-square.size / 2}
              width={square.size}
              height={square.size}
              fill="none"
              stroke={`url(#assembleGradient${(i % 3) + 1})`}
              strokeWidth="1.5"
              filter="url(#assembleGlow)"
              opacity="0.7"
            />
          </g>
        ))}
      </svg>
    </div>
  );
};

